import { useEffect, useRef } from 'react';

export enum ShortCuts {
    Copy,
    Paste,
    Duplicate,
    Undo,
    Redo,
    Search,
    Cut
}

function isEditableElement(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const editableAncestor = target.closest('input, textarea, select, [contenteditable], [contenteditable="true"], [contenteditable="plaintext-only"]');
    return Boolean(editableAncestor) || target.isContentEditable;
}

function hasActiveTextSelection(): boolean {
    const selection = window.getSelection();
    return Boolean(selection && !selection.isCollapsed && selection.toString().length > 0);
}

function shouldUseNativeShortcut(shortcut: ShortCuts, event: KeyboardEvent): boolean {
    const targetIsEditable = isEditableElement(event.target);

    if (shortcut === ShortCuts.Copy || shortcut === ShortCuts.Cut) {
        return targetIsEditable || hasActiveTextSelection();
    }

    if (shortcut === ShortCuts.Paste) {
        return targetIsEditable;
    }

    if (shortcut === ShortCuts.Undo || shortcut === ShortCuts.Redo) {
        return targetIsEditable;
    }

    return false;
}

const useShortcut = (shortcut: ShortCuts, callback: () => void): void => {
    const keyPressing = useRef(false);

    useEffect(() => {
        let keyDown = 'c';
        const handleShortcut = (event: KeyboardEvent): void => {
            const key = event.key.toLowerCase();
            const hasCommandModifier = event.ctrlKey || event.metaKey;

            if (shortcut === ShortCuts.Search) {
                if (!isEditableElement(event.target) && key === '/' && !keyPressing.current) {
                    event.preventDefault();
                    keyPressing.current = true;
                    callback();
                }
                return;
            }

            if (shouldUseNativeShortcut(shortcut, event)) {
                return;
            }

            if (!hasCommandModifier) {
                return;
            }

            switch (shortcut) {
                case ShortCuts.Copy:
                    keyDown = 'c';
                    break;
                case ShortCuts.Paste:
                    keyDown = 'v';
                    break;
                case ShortCuts.Cut:
                    keyDown = 'x';
                    break;
                case ShortCuts.Duplicate:
                    keyDown = 'd';
                    break;
                case ShortCuts.Undo:
                    keyDown = 'z';
                    break;
                case ShortCuts.Redo:
                    keyDown = 'y';
                    break;
            }

            const isUndoWithShift = shortcut === ShortCuts.Undo && event.shiftKey;
            const isRedoWithShift = shortcut === ShortCuts.Redo && key === 'z' && event.shiftKey;
            const matchesShortcutKey = key === keyDown;

            if ((matchesShortcutKey || isRedoWithShift) && !isUndoWithShift) {
                event.preventDefault();
                if (!keyPressing.current) {
                    keyPressing.current = true;
                    callback();
                }
            }
        };

        const handleKeyUp = () => {
            keyPressing.current = false;
        };

        document.addEventListener('keydown', handleShortcut);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('keydown', handleShortcut);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [callback, shortcut]);
};

export default useShortcut;
export { isEditableElement };
